import type {
  ColumnsType,
  ColumnType,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';

import type { ITypedKeyValueObject } from '@/types/common';

export const toQueryFormat = (
  genericFilters,
  pagination: TablePaginationConfig,
  filters: ITypedKeyValueObject<any>,
  sorter: SorterResult<any>,
  extra: TableCurrentDataSource<any>,
) => {
  const { pageSize, current }: TablePaginationConfig = pagination;
  const { field, order }: SorterResult<any> = sorter;
  const normalizedField = Array.isArray(field) ? field.join('.') : field;
  const sort = order ? { sort: `${normalizedField}:${order === 'ascend' ? 1 : -1}` } : {};

  return {
    limit: pageSize,
    skip: pageSize * (current < 1 ? 0 : current - 1),
    ...sort,
    ...genericFilters(filters),
  };
};

/** Key Ant Design uses in `filters` state (from column.dataIndex) */
const getFilterStateKey = (column: ColumnType<any>): string => {
  const { dataIndex } = column;
  return Array.isArray(dataIndex) ? dataIndex.join('.') : String(dataIndex);
};

/** Key sent to the API; may differ when dots in dataIndex break Express qs (e.g. productType vs product.type) */
const getFilterQueryParamKey = (column: ColumnType<any>): string => {
  const custom = (column as ColumnType<any> & { filterQueryParam?: string }).filterQueryParam;
  return custom || getFilterStateKey(column);
};

export const getGenericFilters = (columns: ColumnsType) => {
  return (filtersValue: ITypedKeyValueObject<any>) =>
    columns
      .filter((column: ColumnType<any>) => {
        const { filters, filterDropdown, dataIndex } = column;
        if (!(filters || filterDropdown)) return false;
        const stateKey = getFilterStateKey(column);
        const value = filtersValue[stateKey] ?? filtersValue[String(dataIndex)];
        return Boolean(value);
      })
      .reduce((prev: ITypedKeyValueObject<any>, column: ColumnType<any>) => {
        const stateKey = getFilterStateKey(column);
        const paramKey = getFilterQueryParamKey(column);
        const { dataIndex } = column;
        const raw = filtersValue[stateKey] ?? filtersValue[String(dataIndex)];
        if (raw == null) return prev;
        const list = Array.isArray(raw) ? raw.filter((v) => v != null && v !== '') : [raw];
        if (list.length === 0) return prev;
        const value = list.length === 1 ? list[0] : list.join(',');
        return { ...prev, [paramKey]: value };
      }, {});
};
